// apps/web/src/features/commercial-proposals/public/PublicProposalPhoto.tsx
//
// @aiPurpose render the product photo on the public sheet (#1199). The
//   bytes are fetched from the token-validated streaming endpoint and shown
//   via an object URL — never a durable/presigned URL, and the raw
//   `photoObjectKey` is never exposed (it is not on the public DTO).
// @aiSecurity on any fetch failure render NOTHING (no broken-image leak).
//   The caller only mounts this when `hasPhoto` is true; the component never
//   fetches when there is no photo.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchPublicProposalPhoto } from './publicApi.js';

export function PublicProposalPhoto({ token }: { token: string }) {
  const { t } = useTranslation('commercialProposalsPublic');
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    fetchPublicProposalPhoto(token)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        // Render nothing on failure; never surface a broken image.
        if (active) setSrc(null);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [token]);

  if (!src) return null;
  return (
    <img
      data-testid="cp-public-photo"
      src={src}
      alt={t('photoAlt')}
      // Fill the customer-sheet square photo slot (#1217); the slot owns the
      // radius/aspect framing so the public and internal photos match.
      style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
}
